import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { ReactGrid, Column, Row, CellChange, TextCell, NumberCell, Id, MenuOption, SelectionMode } from "@silevis/reactgrid";
import "@silevis/reactgrid/styles.css";
import { 
    Box, 
    Button, 
    Checkbox, 
    FormControlLabel, 
    Grid, 
    Typography,
    Alert,
    CircularProgress,
    Snackbar,
    LinearProgress,
    Paper,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions
} from '@mui/material';
import { Download, Upload, Save, DeleteOutline } from '@mui/icons-material';
import Loader from '../../../components/Loader';
import { exportBillablesCSVService, importBillablesCSVService, getCSVImportJobStatus } from '../../../services/billing';
import { useSnackBarState } from '../../../hooks';
import { Translate } from 'react-localize-redux';

// Constants
const ACTION_COLUMN_ID = 'action';
const ACTION_COLUMN_WIDTH = 80;
const FIXED_COLUMNS_COUNT = 8; // Including action column
const FIRST_DATA_COLUMN_INDEX = 7; // Where fixed data columns start in CSV

/** Default width for an insurance column based on header text length (min 100, max 250). */
function getDefaultInsuranceColumnWidth(code: string): number {
    const base = 60 + (code?.trim().length ?? 0) * 7;
    return Math.min(250, Math.max(100, base));
}

interface BillableRow {
    serviceType: string;
    category: string;
    serviceCode: string;
    department: string;
    name: string;
    billableType: string;
    available: string;
    insurances: { [code: string]: number };
    action?: '' | 'DELETE'; // Action column for deletion
}

interface BillablesConfigurationProps {
    uuidInvestigation: string;
    idBillingInfo: number;
    onBillablesUpdated: () => void;
}

const BillablesConfiguration: React.FC<BillablesConfigurationProps> = ({
    uuidInvestigation,
    idBillingInfo,
    onBillablesUpdated
}) => {
    // State
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [billables, setBillables] = useState<BillableRow[]>([]);
    const [insuranceCodes, setInsuranceCodes] = useState<string[]>([]);
    const [forceCreateInsurance, setForceCreateInsurance] = useState(true);
    const [forceCoveragePercent, setForceCoveragePercent] = useState(true);
    const [showSnackbar, setShowSnackbar, handleCloseSnackbar] = useSnackBarState();
    const [hasChanges, setHasChanges] = useState(false);
    const [importStatus, setImportStatus] = useState<{
        state: 'idle' | 'uploading' | 'pending' | 'processing' | 'completed' | 'failed';
        message?: string;
        progress?: number;
    }>({ state: 'idle' });
    const [columnWidths, setColumnWidths] = useState<Record<string, number>>({
        [ACTION_COLUMN_ID]: ACTION_COLUMN_WIDTH,
        'serviceType': 120,
        'category': 150,
        'serviceCode': 120,
        'department': 120,
        'name': 250,
        'billableType': 120,
        'available': 100,
    });

    // Fetch billables on mount
    useEffect(() => {
        if (uuidInvestigation && idBillingInfo) {
            fetchBillables();
        }
    }, [uuidInvestigation, idBillingInfo]);

    // Helper function to extract error message
    const extractErrorMessage = (error: any, defaultMessage: string): string => {
        if (error?.response?.data?.errorMessage) {
            return error.response.data.errorMessage;
        } else if (error?.response?.data?.message) {
            return error.response.data.message;
        } else if (error?.response?.data?.error) {
            return error.response.data.error;
        } else if (error?.response?.data && typeof error.response.data === 'string') {
            return error.response.data;
        } else if (error?.message) {
            return error.message;
        } else if (typeof error === 'string') {
            return error;
        }
        return defaultMessage;
    };

    // Helper function to show error snackbar with delay
    const showErrorSnackbar = useCallback((message: string) => {
        setShowSnackbar({ show: false });
        setTimeout(() => {
            setShowSnackbar({
                show: true,
                message: message,
                severity: "error"
            });
        }, 100);
    }, [setShowSnackbar]);

    // Helper to show success notification
    const showSuccessNotification = useCallback((message: string) => {
        setShowSnackbar({
            show: true,
            message: message,
            severity: "info"
        });
    }, [setShowSnackbar]);

    // Poll job status until completion
    const pollJobStatus = async (jobId: string): Promise<void> => {
        const maxAttempts = 300; // 5 minutes max (300 seconds)
        let attempts = 0;

        return new Promise((resolve, reject) => {
            const pollInterval = setInterval(async () => {
                attempts++;
                
                if (attempts > maxAttempts) {
                    clearInterval(pollInterval);
                    setImportStatus({ state: 'failed', message: 'Import timeout - job took too long to complete' });
                    reject(new Error("Import timeout - job took too long to complete"));
                    return;
                }

                try {
                    const status = await getCSVImportJobStatus(uuidInvestigation, jobId);
                    
                    setImportStatus({
                        state: status.state,
                        message: status.message,
                        progress: status.progress
                    });
                    
                    if (status.state === 'completed') {
                        clearInterval(pollInterval);
                        resolve();
                    } else if (status.state === 'failed') {
                        clearInterval(pollInterval);
                        reject(new Error(status.error || status.message || "Import failed"));
                    }
                } catch (error: any) {
                    clearInterval(pollInterval);
                    setImportStatus({ state: 'failed', message: 'Error checking import status' });
                    reject(error);
                }
            }, 1000);
        });
    };

    const fetchBillables = async () => {
        if (!uuidInvestigation || !idBillingInfo) return;
        
        try {
            setLoading(true);
            const response = await exportBillablesCSVService(uuidInvestigation, idBillingInfo);
            
            if (response.status === 200) {
                console.log('=== FETCH BILLABLES DEBUG ===');
                console.log('CSV received length:', response.csv.length);
                console.log('First line of CSV:', response.csv.split('\n')[0].substring(0, 200) + '...');
                
                const parsed = parseCSV(response.csv);
                
                console.log('=== AFTER PARSING ===');
                console.log('Parsed rows:', parsed.rows.length);
                console.log('Parsed insurance codes:', parsed.insuranceCodes.length);
                console.log('ANCAR in parsed codes:', parsed.insuranceCodes.includes('IPM ANCAR'));
                console.log('All insurances with ANCAR:', parsed.insuranceCodes.filter(c => c.includes('ANCAR')));
                
                if (parsed.rows.length > 0) {
                    console.log('First row insurance keys:', Object.keys(parsed.rows[0].insurances).length);
                    console.log('First row has ANCAR:', 'IPM ANCAR' in parsed.rows[0].insurances);
                    console.log('First row ANCAR value:', parsed.rows[0].insurances['IPM ANCAR']);
                }
                
                setBillables(parsed.rows);
                setInsuranceCodes(parsed.insuranceCodes);
                setColumnWidths(prev => {
                    const next = { ...prev };
                    parsed.insuranceCodes.forEach((code: string) => {
                        if (next[code] == null) {
                            next[code] = getDefaultInsuranceColumnWidth(code);
                        }
                    });
                    return next;
                });
                setHasChanges(false);
            }
        } catch (error: any) {
            const errorMessage = extractErrorMessage(error, "hospital.billing.billables.error.load");
            showErrorSnackbar(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    // Parse CSV into structured data
    const parseCSV = (csvContent: string): { rows: BillableRow[], insuranceCodes: string[] } => {
        const lines = csvContent.trim().split('\n');
        if (lines.length === 0) {
            return { rows: [], insuranceCodes: [] };
        }
        
        const headers = parseCSVLine(lines[0]);
        
        // Check if Action column exists (first column)
        const hasActionColumn = headers[0]?.toLowerCase() === 'action';
        const dataStartIndex = hasActionColumn ? 1 : 0;
        const insuranceStartIndex = hasActionColumn ? FIXED_COLUMNS_COUNT : FIXED_COLUMNS_COUNT - 1;
        const insuranceCodes = headers.slice(insuranceStartIndex);
        
        console.log('=== parseCSV DEBUG ===');
        console.log('Total headers:', headers.length);
        console.log('Insurance start index:', insuranceStartIndex);
        console.log('Insurance codes extracted:', insuranceCodes.length);
        console.log('ANCAR in insuranceCodes:', insuranceCodes.filter(code => code.includes('ANCAR')));
        console.log('First 10 insurance codes:', insuranceCodes.slice(0, 10));
        console.log('Last 10 insurance codes:', insuranceCodes.slice(-10));
        
        if (lines.length === 1) {
            return { rows: [], insuranceCodes };
        }
        
        const rows: BillableRow[] = lines.slice(1).map(line => {
            const values = parseCSVLine(line);
            const insurances: { [code: string]: number } = {};
            
            insuranceCodes.forEach((code, index) => {
                insurances[code] = parseInt(values[insuranceStartIndex + index] || '0');
            });
            
            // Get action value if present (first column)
            const actionValue = hasActionColumn ? values[0] : '';
            
            return {
                action: actionValue?.toUpperCase() === 'DELETE' ? 'DELETE' : '',
                serviceType: values[dataStartIndex + 0] || '',
                category: values[dataStartIndex + 1] || '',
                serviceCode: values[dataStartIndex + 2] || '',
                department: values[dataStartIndex + 3] || '',
                name: values[dataStartIndex + 4] || '',
                billableType: values[dataStartIndex + 5] || '',
                available: values[dataStartIndex + 6] || '',
                insurances
            };
        });
        
        return { rows, insuranceCodes };
    };

    // Handle CSV quoted fields properly
    const parseCSVLine = (line: string): string[] => {
        const result: string[] = [];
        let current = '';
        let inQuotes = false;
        
        for (let i = 0; i < line.length; i++) {
            const char = line[i];
            
            if (char === '"') {
                if (inQuotes && line[i + 1] === '"') {
                    current += '"';
                    i++;
                } else {
                    inQuotes = !inQuotes;
                }
            } else if (char === ',' && !inQuotes) {
                result.push(current);
                current = '';
            } else {
                current += char;
            }
        }
        result.push(current);
        
        return result.map(v => v.trim());
    };

    // Convert back to CSV
    const generateCSV = (): string => {
        const headers = [
            'Action',
            'ServiceType', 'Category', 'ServiceCode', 'Department', 
            'Name', 'BillableType', 'Available', 
            ...insuranceCodes
        ];
        
        const rows = billables.map(row => {
            const escapeName = (name: string) => {
                if (name.includes(',') || name.includes('"') || name.includes('\n')) {
                    return `"${name.replace(/"/g, '""')}"`;
                }
                return name;
            };
            
            const actionCol = row.action || '';
            const fixedCols = [
                row.serviceType,
                row.category,
                row.serviceCode,
                row.department,
                escapeName(row.name),
                row.billableType,
                row.available
            ];
            
            const insuranceCols = insuranceCodes.map(code => row.insurances[code] || 0);
            
            return [actionCol, ...fixedCols, ...insuranceCols].join(',');
        });
        
        return [headers.join(','), ...rows].join('\n');
    };

    // Save changes
    const handleSave = async () => {
        if (!uuidInvestigation || !idBillingInfo) return;
        
        try {
            setSaving(true);
            setImportStatus({ state: 'uploading', message: 'Uploading CSV data...' });
            
            const csvContent = generateCSV();
            const response = await importBillablesCSVService(
                uuidInvestigation,
                idBillingInfo,
                csvContent,
                forceCreateInsurance,
                forceCoveragePercent
            );
            
            if (response.status === 202 && response.jobId) {
                setImportStatus({ state: 'processing', message: 'Processing import...' });
                await pollJobStatus(response.jobId);
                
                setImportStatus({ state: 'completed', message: 'Import completed successfully!' });
                setShowSnackbar({
                    show: true,
                    message: "hospital.billing.billables.success.updated",
                    severity: "success"
                });
                setHasChanges(false);
                onBillablesUpdated();
                await fetchBillables();
                
                setTimeout(() => {
                    setImportStatus({ state: 'idle' });
                }, 3000);
                
            } else if (response.status >= 200 && response.status < 300) {
                setImportStatus({ state: 'completed', message: 'Billables updated successfully!' });
                setShowSnackbar({
                    show: true,
                    message: "hospital.billing.billables.success.updated",
                    severity: "success"
                });
                setHasChanges(false);
                onBillablesUpdated();
                await fetchBillables();
                
                setTimeout(() => {
                    setImportStatus({ state: 'idle' });
                }, 3000);
            } else {
                const errorMessage = response.message || "hospital.billing.billables.error.save";
                setImportStatus({ state: 'failed', message: errorMessage });
                showErrorSnackbar(errorMessage);
            }
        } catch (error: any) {
            const errorMessage = extractErrorMessage(error, "hospital.billing.billables.error.save");
            setImportStatus({ state: 'failed', message: errorMessage });
            showErrorSnackbar(errorMessage);
        } finally {
            setSaving(false);
        }
    };

    // Export as CSV file
    const handleExport = () => {
        const csvContent = generateCSV();
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `billables_${uuidInvestigation}_${Date.now()}.csv`;
        link.click();
        URL.revokeObjectURL(link.href);
    };

    // Import from CSV file
    const handleImport = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;
        
        const reader = new FileReader();
        reader.onload = (e) => {
            const content = e.target?.result as string;
            const parsed = parseCSV(content);
            setBillables(parsed.rows);
            setInsuranceCodes(parsed.insuranceCodes);
            setColumnWidths(prev => {
                const next = { ...prev };
                parsed.insuranceCodes.forEach((code: string) => {
                    if (next[code] == null) {
                        next[code] = getDefaultInsuranceColumnWidth(code);
                    }
                });
                return next;
            });
            setHasChanges(true);
        };
        reader.readAsText(file);
        
        event.target.value = '';
    };

    // ReactGrid columns configuration
    const columns = useMemo((): Column[] => {
        console.log('=== COLUMNS MEMO RECALCULATING ===');
        console.log('insuranceCodes.length:', insuranceCodes.length);
        console.log('ANCAR in insuranceCodes:', insuranceCodes.includes('IPM ANCAR'));
        console.log('All ANCAR codes:', insuranceCodes.filter(c => c.includes('ANCAR')));
        
        const fixedColumns: Column[] = [
            { columnId: ACTION_COLUMN_ID, width: columnWidths[ACTION_COLUMN_ID], resizable: false },
            { columnId: 'serviceType', width: columnWidths['serviceType'], resizable: true },
            { columnId: 'category', width: columnWidths['category'], resizable: true },
            { columnId: 'serviceCode', width: columnWidths['serviceCode'], resizable: true },
            { columnId: 'department', width: columnWidths['department'], resizable: true },
            { columnId: 'name', width: columnWidths['name'], resizable: true },
            { columnId: 'billableType', width: columnWidths['billableType'], resizable: true },
            { columnId: 'available', width: columnWidths['available'], resizable: true },
        ];
        
        const insuranceColumns: Column[] = insuranceCodes.map(code => ({
            columnId: code,
            width: columnWidths[code] ?? getDefaultInsuranceColumnWidth(code),
            resizable: true
        }));
        
        console.log('Total columns created:', fixedColumns.length + insuranceColumns.length);
        console.log('Insurance columns created:', insuranceColumns.length);
        console.log('ANCAR column exists:', insuranceColumns.some(col => col.columnId === 'IPM ANCAR'));
        
        return [...fixedColumns, ...insuranceColumns];
    }, [insuranceCodes, columnWidths]);

    // ReactGrid rows
    const rows = useMemo((): Row[] => {
        console.log('=== ROWS MEMO RECALCULATING ===');
        console.log('Billables count:', billables.length);
        console.log('Insurance codes for header:', insuranceCodes.length);
        console.log('ANCAR in header row:', insuranceCodes.includes('IPM ANCAR'));
        
        const headerRow: Row = {
            rowId: 'header',
            cells: [
                { type: 'header', text: 'Action' },
                { type: 'header', text: 'Service Type' },
                { type: 'header', text: 'Category' },
                { type: 'header', text: 'Service Code' },
                { type: 'header', text: 'Department' },
                { type: 'header', text: 'Name' },
                { type: 'header', text: 'Billable Type' },
                { type: 'header', text: 'Available' },
                ...insuranceCodes.map(code => ({ type: 'header' as const, text: code }))
            ]
        };
        
        console.log('Header cells created:', headerRow.cells.length);
        console.log('ANCAR in header cells:', headerRow.cells.some(cell => cell.text === 'IPM ANCAR'));
        
        const dataRows: Row[] = billables.map((billable, index) => {
            const isDeleted = billable.action === 'DELETE';
            return {
                rowId: index,
                cells: [
                    { 
                        type: 'text', 
                        text: isDeleted ? '❌' : '🗑️', 
                        nonEditable: false,
                        className: isDeleted ? 'deleted-row' : ''
                    },
                    { 
                        type: 'text', 
                        text: billable.serviceType, 
                        nonEditable: false,
                        className: isDeleted ? 'deleted-row' : ''
                    },
                    { 
                        type: 'text', 
                        text: billable.category,
                        className: isDeleted ? 'deleted-row' : ''
                    },
                    { 
                        type: 'text', 
                        text: billable.serviceCode,
                        className: isDeleted ? 'deleted-row' : ''
                    },
                    { 
                        type: 'text', 
                        text: billable.department,
                        className: isDeleted ? 'deleted-row' : ''
                    },
                    { 
                        type: 'text', 
                        text: billable.name,
                        className: isDeleted ? 'deleted-row' : ''
                    },
                    { 
                        type: 'text', 
                        text: billable.billableType,
                        className: isDeleted ? 'deleted-row' : ''
                    },
                    { 
                        type: 'text', 
                        text: billable.available,
                        className: isDeleted ? 'deleted-row' : ''
                    },
                    ...insuranceCodes.map(code => ({
                        type: 'number' as const,
                        value: billable.insurances[code] || 0,
                        className: isDeleted ? 'deleted-row' : ''
                    }))
                ]
            };
        });
        
        // Add footer row with "+" button to add new rows
        const footerRow: Row = {
            rowId: 'add-row-footer',
            cells: [
                { 
                    type: 'text', 
                    text: '➕',
                    nonEditable: false,
                    className: 'add-row-cell'
                },
                { 
                    type: 'text', 
                    text: 'Click + to add a new row',
                    nonEditable: true,
                    className: 'add-row-hint'
                },
                ...Array(6 + insuranceCodes.length).fill(null).map(() => ({
                    type: 'text' as const,
                    text: '',
                    nonEditable: true
                }))
            ]
        };
        
        return [headerRow, ...dataRows, footerRow];
    }, [billables, insuranceCodes]);

    // Handle changes - toggle action on click, update other cells
    const handleChanges = useCallback((changes: CellChange[]) => {
        const updatedBillables = [...billables];
        
        changes.forEach(change => {
            const rowIndex = change.rowId as number;
            const columnIndex = columns.findIndex(col => col.columnId === change.columnId);
            
            if (rowIndex >= 0 && columnIndex >= 0) {
                const columnId = change.columnId as string;
                
                // Handle action column toggle
                if (columnId === ACTION_COLUMN_ID) {
                    const currentAction = updatedBillables[rowIndex].action;
                    updatedBillables[rowIndex].action = currentAction === 'DELETE' ? '' : 'DELETE';
                }
                // Handle fixed columns (exclude action column)
                else if (columnIndex < FIXED_COLUMNS_COUNT && columnId !== ACTION_COLUMN_ID) {
                    const newCell = change.newCell as TextCell | NumberCell;
                    const newValue = 'text' in newCell ? newCell.text : newCell.value;
                    const fieldName = columnId as keyof Omit<BillableRow, 'insurances' | 'action'>;
                    (updatedBillables[rowIndex] as any)[fieldName] = newValue;
                }
                // Handle insurance columns
                else {
                    const newCell = change.newCell as NumberCell;
                    updatedBillables[rowIndex].insurances[columnId] = Number(newCell.value) || 0;
                }
                
                setHasChanges(true);
            }
        });
        
        setBillables(updatedBillables);
    }, [billables, columns]);

    // Handle focus changes - trigger action toggle on click or add row
    const handleFocusLocationChange = useCallback((location: { rowId: Id; columnId: Id }) => {
        // Handle add row footer click
        if (location.rowId === 'add-row-footer' && location.columnId === ACTION_COLUMN_ID) {
            const newRow: BillableRow = {
                serviceType: '',
                category: '',
                serviceCode: '',
                department: '',
                name: '',
                billableType: '',
                available: '',
                insurances: {},
                action: ''
            };
            // Initialize all insurance codes with 0
            insuranceCodes.forEach(code => {
                newRow.insurances[code] = 0;
            });
            setBillables([...billables, newRow]);
            setHasChanges(true);
            showSuccessNotification('New row added. Remember to save your changes.');
            return;
        }
        
        if (location.columnId === ACTION_COLUMN_ID && typeof location.rowId === 'number') {
            const updatedBillables = [...billables];
            const rowIndex = location.rowId;
            const row = updatedBillables[rowIndex];
            const currentAction = row.action;
            
            // Check if row is empty (all data fields are empty or 0)
            const hasInsuranceData = Object.values(row.insurances).some(val => val && val > 0);
            const isEmptyRow = !row.serviceType?.trim() && 
                              !row.category?.trim() && 
                              !row.serviceCode?.trim() && 
                              !row.department?.trim() && 
                              !row.name?.trim() && 
                              !row.billableType?.trim() && 
                              !row.available?.trim() &&
                              !hasInsuranceData;
            
            if (isEmptyRow) {
                // Remove empty row entirely
                updatedBillables.splice(rowIndex, 1);
                showSuccessNotification('Empty row removed.');
            } else {
                // Toggle DELETE action for non-empty rows
                updatedBillables[rowIndex].action = currentAction === 'DELETE' ? '' : 'DELETE';
            }
            
            setBillables(updatedBillables);
            setHasChanges(true);
        }
    }, [billables, insuranceCodes, showSuccessNotification]);

    // Handle column resize
    const handleColumnResize = useCallback((columnId: Id, width: number, selectedColIds: Id[]) => {
        setColumnWidths(prev => ({
            ...prev,
            [columnId as string]: width
        }));
    }, []);

    if (loading) {
        return <Loader />;
    }

    if (!idBillingInfo) {
        return (
            <Alert severity="error">
                <Translate id="hospital.billing.no_billing_info" />
            </Alert>
        );
    }

    const showDebug = typeof window !== 'undefined' && window.location.search.includes('debug=1');
    const ancarInList = insuranceCodes.some(c => c.includes('ANCAR'));
    const ancarExact = insuranceCodes.find(c => c.includes('ANCAR'));

    return (
        <Box sx={{ p: 2 }}>
            {showDebug && (
                <Alert severity="info" sx={{ mb: 2 }} data-debug-billables>
                    DEBUG: Insurances count={insuranceCodes.length} | ANCAR in list={String(ancarInList)} | ANCAR exact=&quot;{ancarExact ?? '\u2014'}&quot; | Sample: {insuranceCodes.slice(0, 3).join(', ')}...
                </Alert>
            )}
            {/* Action Bar */}
            <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
                <Grid item>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <Save />}
                        onClick={handleSave}
                        disabled={!hasChanges || loading || saving}
                    >
                        {saving ? 'Saving...' : <Translate id="hospital.billing.billables.save" />}
                    </Button>
                </Grid>
                <Grid item>
                    <Button
                        variant="outlined"
                        startIcon={<Download />}
                        onClick={handleExport}
                        disabled={loading || saving || billables.length === 0}
                    >
                        <Translate id="hospital.billing.billables.export" />
                    </Button>
                </Grid>
                <Grid item>
                    <Button
                        variant="outlined"
                        component="label"
                        startIcon={<Upload />}
                        disabled={loading || saving}
                    >
                        <Translate id="hospital.billing.billables.import" />
                        <input
                            type="file"
                            hidden
                            accept=".csv"
                            onChange={handleImport}
                        />
                    </Button>
                </Grid>
                <Grid item xs />
                <Grid item>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={forceCreateInsurance}
                                onChange={(e) => setForceCreateInsurance(e.target.checked)}
                                disabled={saving}
                            />
                        }
                        label={<Translate id="hospital.billing.billables.forceCreateInsurance" />}
                    />
                </Grid>
                <Grid item>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={forceCoveragePercent}
                                onChange={(e) => setForceCoveragePercent(e.target.checked)}
                                disabled={saving}
                            />
                        }
                        label={<Translate id="hospital.billing.billables.forceCoveragePercent" />}
                    />
                </Grid>
            </Grid>

            {/* Import Status Banner */}
            {importStatus.state !== 'idle' && (
                <Paper 
                    elevation={3} 
                    sx={{ 
                        p: 2, 
                        mb: 2,
                        backgroundColor: 
                            importStatus.state === 'completed' ? '#e8f5e9' : 
                            importStatus.state === 'failed' ? '#ffebee' : 
                            '#e3f2fd',
                        borderLeft: `4px solid ${
                            importStatus.state === 'completed' ? '#4caf50' : 
                            importStatus.state === 'failed' ? '#f44336' : 
                            '#2196f3'
                        }`
                    }}
                >
                    <Grid container alignItems="center" spacing={2}>
                        <Grid item>
                            {(importStatus.state === 'uploading' || importStatus.state === 'pending' || importStatus.state === 'processing') ? (
                                <CircularProgress size={24} />
                            ) : importStatus.state === 'completed' ? (
                                <Box sx={{ color: '#4caf50', fontSize: '24px' }}>✓</Box>
                            ) : (
                                <Box sx={{ color: '#f44336', fontSize: '24px' }}>✗</Box>
                            )}
                        </Grid>
                        <Grid item xs>
                            <Typography variant="body1" fontWeight="bold">
                                {importStatus.state === 'uploading' && 'Uploading CSV...'}
                                {importStatus.state === 'pending' && 'Import Queued...'}
                                {importStatus.state === 'processing' && 'Processing Import...'}
                                {importStatus.state === 'completed' && 'Import Completed Successfully'}
                                {importStatus.state === 'failed' && 'Import Failed'}
                            </Typography>
                            {importStatus.message && (
                                <Typography variant="body2" color="text.secondary">
                                    {importStatus.message}
                                </Typography>
                            )}
                            {importStatus.progress !== undefined && (
                                <Box sx={{ mt: 1 }}>
                                    <LinearProgress 
                                        variant="determinate" 
                                        value={importStatus.progress} 
                                        sx={{ height: 8, borderRadius: 4 }}
                                    />
                                    <Typography variant="caption" color="text.secondary">
                                        {importStatus.progress}% complete
                                    </Typography>
                                </Box>
                            )}
                        </Grid>
                    </Grid>
                    {(importStatus.state === 'uploading' || importStatus.state === 'pending' || importStatus.state === 'processing') && (
                        <LinearProgress sx={{ mt: 2, borderRadius: 2 }} />
                    )}
                </Paper>
            )}

            {/* Excel-like Table or Empty State */}
            {billables.length === 0 ? (
                <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="h6" gutterBottom>
                        No billables found
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                        To get started, import a CSV file with your billables configuration.
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                        Click the "Import CSV" button above to upload your billables file.
                    </Typography>
                </Alert>
            ) : (
                <Box
                    id="billables-grid-container"
                    sx={{ 
                    width: '100%', 
                    height: '600px', 
                    overflow: 'auto', 
                    border: '1px solid #ddd', 
                    backgroundColor: '#fff',
                    '& .rg-cell:first-of-type': {
                        cursor: 'pointer',
                        transition: 'background-color 0.2s',
                        textAlign: 'center',
                        fontSize: '18px',
                        '&:hover': {
                            backgroundColor: '#f0f0f0',
                        }
                    },
                    '& .deleted-row': {
                        backgroundColor: '#ffcccc !important',
                        textDecoration: 'line-through',
                        opacity: 0.8,
                        color: '#666'
                    },
                    '& .add-row-cell': {
                        backgroundColor: '#e3f2fd !important',
                        fontSize: '20px',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        '&:hover': {
                            backgroundColor: '#bbdefb !important',
                        }
                    },
                    '& .add-row-hint': {
                        backgroundColor: '#e3f2fd !important',
                        fontStyle: 'italic',
                        color: '#1976d2'
                    }
                }}>
                    <ReactGrid
                        rows={rows}
                        columns={columns}
                        onCellsChanged={handleChanges}
                        onFocusLocationChanged={handleFocusLocationChange}
                        onColumnResized={handleColumnResize}
                        enableRangeSelection
                        enableFillHandle
                        enableColumnSelection
                        enableRowSelection
                        stickyTopRows={1}
                    />
                </Box>
            )}

            {/* Info Message */}
            <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2">
                    <strong><Translate id="hospital.billing.billables.guide.title" /></strong>
                </Typography>
                <ul style={{ marginTop: '0.5rem', marginBottom: 0 }}>
                    <li>Click the trash icon (🗑️) to mark a row for deletion - it will turn red with an ❌</li>
                    <li>Click the ❌ icon to undo and restore the row</li>
                    <li><Translate id="hospital.billing.billables.guide.readonly" /></li>
                    <li><Translate id="hospital.billing.billables.guide.editable" /></li>
                    <li><Translate id="hospital.billing.billables.guide.insurance" /></li>
                    <li><Translate id="hospital.billing.billables.guide.changes" /></li>
                    <li>If no data is shown, use "Import CSV" to load your billables configuration</li>
                </ul>
            </Alert>

            {/* Snackbar for feedback messages */}
            <Snackbar
                open={showSnackbar.show}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert 
                    onClose={handleCloseSnackbar} 
                    severity={showSnackbar.severity || 'info'}
                    sx={{ width: '100%', minWidth: '400px' }}
                >
                    {showSnackbar.message && (
                        typeof showSnackbar.message === 'string' && showSnackbar.message.startsWith('hospital.') 
                            ? <Translate id={showSnackbar.message} />
                            : showSnackbar.message
                    )}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default BillablesConfiguration;
